import React, { Component } from 'react';
import { Button, Tooltip, Spin, Modal } from 'antd';
import { ReloadOutlined, PlusOutlined, FolderOpenOutlined, DatabaseOutlined } from '@ant-design/icons';
import qs from 'qs';
import { fetchData } from '../../../lib/apis';
import { AccountMetaData } from '../../../config/settings';
import ProductListFilters from './components/ProductListFilters';
import ProductListTable from './components/ProductListTable';
import ProductDrawer from './components/ProductDrawer';
import ProductKnowledgeReviewModal from './components/ProductKnowledgeReviewModal';
import { aiConfigApi } from '../../../lib/aiConfigApi';

function normalizeAttachment(item) {
    if (!item) return null;
    const responseAttachment = item.response && item.response.attachment ? item.response.attachment : null;
    const url = responseAttachment ? responseAttachment.url : item.url;
    if (!url) return null;
    return {
        type: responseAttachment ? responseAttachment.type : item.type,
        url,
        name: responseAttachment ? responseAttachment.name : item.name
    };
}

function getAttachmentsFromFileList(fileList = []) {
    return fileList
        .filter((item) => item && item.status === 'done')
        .map((item) => normalizeAttachment(item))
        .filter(Boolean);
}

export default class ProductIndexView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            listData: [],
            listCate: [],
            total: 0,
            loading: false,
            drawerVisible: false,
            editingProduct: null,
            fileList: [],
            knowledgePreparing: false,
            knowledgeCommitting: false,
            knowledgeDownloading: false,
            knowledgeReviewVisible: false,
            knowledgeDraft: null,
            knowledgeProgress: null,
            knowledgeDocument: null,
            knowledgeDocumentLoading: false,
            knowledgeDocumentError: ''
        };
        this.limit = 20;
        this.page = 1;
    }

    getApiBaseUrl = () => {
        return (window._env_ && window._env_.REACT_APP_API_CHOTSALE_URL) || process.env.REACT_APP_API_CHOTSALE_URL || window.location.origin;
    }

    componentDidMount() {
        this.getListCate();
        this.getListData();
    }

    getListCate = async () => {
        const res = await fetchData({ url: 'api/v1/product/cate/list?limit=200' });
        this.setState({ listCate: res.data || [] });
    }

    getListData = async () => {
        try {
            this.setState({ loading: true });
            const params = {
                page: this.page,
                limit: this.limit,
                ...(this.formFilter ? this.formFilter.getFieldsValue() : {})
            };
            const res = await fetchData({ url: `api/v1/product/summary?${qs.stringify(params)}` });
            this.setState({ listData: res.data || [], total: res.total || 0, loading: false });
        } catch (err) {
            this.setState({ loading: false });
            this.props.notification({ title: 'Sản phẩm', content: err.message || 'Đã có lỗi xảy ra', type: 'error' });
        }
    }

    showAgentConfigurationRequired = () => {
        Modal.confirm({
            title: 'Cần cấu hình Agent AI',
            content: 'Để tạo và nạp bộ tri thức sản phẩm, công ty cần có cấu hình Agent AI trước.',
            okText: 'Đến trang cấu hình',
            cancelText: 'Để sau',
            onOk: () => this.props.history.push('/ai-chatbot')
        });
    }

    startKnowledgeProgress = (phase, message) => {
        this.setState({
            knowledgeProgress: {
                phase,
                stage: 'starting',
                message,
                current: 0,
                total: 0,
                events: []
            }
        });
    }

    updateKnowledgeProgress = (event) => {
        this.setState((previousState) => {
            const previous = previousState.knowledgeProgress || { events: [] };
            const progressEvent = {
                ...event,
                id: `${Date.now()}-${Math.random()}`
            };
            return {
                knowledgeProgress: {
                    ...previous,
                    ...event,
                    events: [...(previous.events || []), progressEvent].slice(-12)
                }
            };
        });
    }

    consumeKnowledgeProgressStream = async (path, payload, onProgress) => {
        const response = await fetch(
            `${this.getApiBaseUrl().replace(/\/$/, '')}/${path}`,
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Authorization: AccountMetaData.getToken(),
                    Company: AccountMetaData.getCompanyId(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload || {})
            }
        );

        if (!response.ok) {
            let responseError = null;
            try {
                responseError = await response.json();
            } catch (error) {
                // API proxy/load balancer may return a non-JSON response.
            }
            const streamError = new Error(responseError?.message || 'Không thể mở luồng tiến độ tri thức sản phẩm');
            streamError.code = responseError?.code || response.status;
            streamError.errorCode = responseError?.errorCode;
            throw streamError;
        }

        if (!response.body || typeof response.body.getReader !== 'function') {
            throw new Error('Trình duyệt không hỗ trợ hiển thị tiến độ nạp tri thức');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let completed = false;
        let result;
        let streamError;

        const parseFrame = (frame) => {
            const lines = frame.split('\n');
            let eventName = 'message';
            const dataLines = [];
            lines.forEach((line) => {
                if (line.startsWith('event:')) eventName = line.slice(6).trim();
                if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
            });
            if (dataLines.length === 0) return;

            let data;
            try {
                data = JSON.parse(dataLines.join('\n'));
            } catch (error) {
                return;
            }

            if (eventName === 'progress') {
                onProgress?.(data);
                return;
            }
            if (eventName === 'complete') {
                completed = true;
                result = data?.data;
                return;
            }
            if (eventName === 'error') {
                const error = new Error(data?.message || 'Nạp tri thức sản phẩm thất bại');
                error.code = data?.code;
                error.errorCode = data?.errorCode;
                streamError = error;
            }
        };

        const consumeChunk = (value) => {
            buffer += value.replace(/\r/g, '');
            let separatorIndex = buffer.indexOf('\n\n');
            while (separatorIndex >= 0) {
                parseFrame(buffer.slice(0, separatorIndex));
                buffer = buffer.slice(separatorIndex + 2);
                separatorIndex = buffer.indexOf('\n\n');
            }
        };

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            consumeChunk(decoder.decode(value, { stream: true }));
        }
        consumeChunk(decoder.decode());
        if (buffer.trim()) parseFrame(buffer);

        if (streamError) throw streamError;
        if (!completed) {
            throw new Error('Luồng tiến độ bị đóng trước khi hoàn tất xử lý');
        }
        return result;
    }

    prepareProductKnowledge = async () => {
        const companyId = this.props.companySelected?.company_id || AccountMetaData.getCompanyId();
        if (!companyId) {
            this.props.notification({
                title: 'Tri thức sản phẩm',
                content: 'Không xác định được công ty hiện tại',
                type: 'error'
            });
            return;
        }

        try {
            const config = await aiConfigApi.getConfig(companyId);
            if (!config || !config.data) {
                this.showAgentConfigurationRequired();
                return;
            }

            this.setState({
                knowledgePreparing: true,
                knowledgeReviewVisible: true,
                knowledgeDraft: null,
                knowledgeDocument: null,
                knowledgeDocumentLoading: false,
                knowledgeDocumentError: ''
            });
            this.startKnowledgeProgress('prepare', 'Đang bắt đầu tạo bản review tri thức sản phẩm');
            const draft = await this.consumeKnowledgeProgressStream(
                'api/v1/product/knowledge/preview/stream',
                {},
                this.updateKnowledgeProgress
            );
            this.setState({ knowledgeDraft: draft || null });
            if (draft?.id) this.loadProductKnowledgeDocx(draft);
        } catch (error) {
            if (error?.errorCode === 'AGENT_NOT_CONFIGURED' || Number(error?.code) === 404) {
                this.setState({ knowledgeReviewVisible: false });
                this.showAgentConfigurationRequired();
            } else {
                this.props.notification({
                    title: 'Tri thức sản phẩm',
                    content: error?.message || 'Không thể tạo bản review sản phẩm',
                    type: 'error'
                });
                this.setState({ knowledgeReviewVisible: false });
            }
        } finally {
            this.setState({ knowledgePreparing: false });
        }
    }

    fetchProductKnowledgeDocx = async (draft) => {
        const response = await fetch(
            `${this.getApiBaseUrl().replace(/\/$/, '')}/api/v1/product/knowledge/${draft.id}/docx`,
            {
                credentials: 'include',
                headers: {
                    Authorization: AccountMetaData.getToken(),
                    Company: AccountMetaData.getCompanyId()
                }
            }
        );
        if (!response.ok) throw new Error('Không thể tải tài liệu DOCX');
        return response.blob();
    }

    loadProductKnowledgeDocx = async (draft) => {
        this.setState({ knowledgeDocumentLoading: true, knowledgeDocumentError: '' });
        try {
            const document = await this.fetchProductKnowledgeDocx(draft);
            if (this.state.knowledgeDraft?.id === draft.id) {
                this.setState({ knowledgeDocument: document });
            }
        } catch (error) {
            if (this.state.knowledgeDraft?.id === draft.id) {
                this.setState({ knowledgeDocumentError: error.message || 'Không thể tải tài liệu DOCX' });
            }
        } finally {
            if (this.state.knowledgeDraft?.id === draft.id) {
                this.setState({ knowledgeDocumentLoading: false });
            }
        }
    }

    downloadProductKnowledgeDocx = async () => {
        const draft = this.state.knowledgeDraft;
        if (!draft?.id) return;
        this.setState({ knowledgeDownloading: true });
        try {
            const blob = this.state.knowledgeDocument || await this.fetchProductKnowledgeDocx(draft);
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = draft.fileName || 'tri-thuc-san-pham.docx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            this.props.notification({
                title: 'Tri thức sản phẩm',
                content: error.message || 'Không thể tải tài liệu DOCX',
                type: 'error'
            });
        } finally {
            this.setState({ knowledgeDownloading: false });
        }
    }

    commitProductKnowledge = async () => {
        const draft = this.state.knowledgeDraft;
        if (!draft?.id) return;
        this.setState({ knowledgeCommitting: true });
        this.startKnowledgeProgress('commit', 'Đang bắt đầu nạp tri thức sản phẩm');
        try {
            await this.consumeKnowledgeProgressStream(
                `api/v1/product/knowledge/${draft.id}/commit/stream`,
                {},
                this.updateKnowledgeProgress
            );
            this.setState({
                knowledgeReviewVisible: false,
                knowledgeDraft: null,
                knowledgeDocument: null,
                knowledgeDocumentError: ''
            });
            this.props.notification({
                title: 'Tri thức sản phẩm',
                content: 'Đã nạp bộ tri thức sản phẩm vào kho dữ liệu chung',
                type: 'success'
            });
        } catch (error) {
            this.props.notification({
                title: 'Tri thức sản phẩm',
                content: error?.message || 'Nạp tri thức sản phẩm thất bại',
                type: 'error'
            });
        } finally {
            this.setState({ knowledgeCommitting: false });
        }
    }

    saveProduct = async (values, variantList = []) => {
        try {
            if ((this.state.fileList || []).some((item) => item && item.status === 'uploading')) {
                throw new Error('Ảnh đang tải lên, vui lòng đợi hoàn tất trước khi lưu');
            }
            if ((variantList || []).some((item) => item && item._imageUploading)) {
                throw new Error('Ảnh của biến thể đang tải lên, vui lòng đợi hoàn tất trước khi lưu');
            }

            const payload = {
                ...values,
                status: values.status ? 'active' : 'inactive',
                attachments: getAttachmentsFromFileList(this.state.fileList)
            };

            const isUpdate = !!values._id;
            const response = await fetchData({
                url: isUpdate ? `api/v1/product/${values._id}` : 'api/v1/product',
                method: isUpdate ? 'put' : 'post',
                data: payload
            });

            const productId = isUpdate ? values._id : (response.data && response.data._id);
            if (!productId) {
                throw new Error('Không lấy được product_id sau khi lưu sản phẩm');
            }

            const existingProduct = this.state.listData.find((item) => item._id === productId) || this.state.editingProduct || null;
            const existingVariants = existingProduct && existingProduct.variants ? existingProduct.variants : [];
            const existingVariantIds = existingVariants.map((item) => item._id).filter(Boolean);
            const nextVariantIds = variantList.map((item) => item._id).filter(Boolean);
            const variantIdsToDelete = existingVariantIds.filter((id) => nextVariantIds.indexOf(id) === -1);

            for (const variantId of variantIdsToDelete) {
                await fetchData({
                    url: `api/v1/product/variants/${variantId}`,
                    method: 'delete'
                });
            }

            for (const variant of variantList) {
                const payloadVariant = {
                    product_id: productId,
                    sku: variant.sku,
                    name: variant.name,
                    barcode: variant.barcode,
                    sale_price: variant.sale_price,
                    status: variant.status,
                    attributes: variant.attributes || {},
                    image: variant.image || '',
                    shipping: variant.shipping || { weight: 0, length: 0, width: 0, height: 0 }
                };

                if (variant._id) {
                    await fetchData({
                        url: `api/v1/product/variants/${variant._id}`,
                        method: 'put',
                        data: payloadVariant
                    });
                } else {
                    await fetchData({
                        url: 'api/v1/product/variants',
                        method: 'post',
                        data: payloadVariant
                    });
                }
            }

            await this.getListData();
            const refreshedProduct = (this.state.listData || []).find((item) => item._id === productId) || null;
            this.setState({ editingProduct: refreshedProduct || this.state.editingProduct });
            this.props.notification({ title: 'Sản phẩm', content: 'Lưu sản phẩm thành công', type: 'success' });
        } catch (err) {
            this.props.notification({ title: 'Sản phẩm', content: err.message || 'Lưu sản phẩm thất bại', type: 'error' });
            throw err;
        }
    }

    render() {
        return (
            <div className="list-connect content-m">
                <div className="list-c">
                    <div className="table-header">
                        <h3>Quản lý sản phẩm: {this.state.total}</h3>
                        <div className="d-flex overflow-auto" style={{ marginBottom: 15 }}>
                            <Tooltip title="Làm mới"><Button className="mr-2" icon={<ReloadOutlined />} onClick={this.getListData} /></Tooltip>
                            <Tooltip title="Thêm sản phẩm"><Button className="mr-2" type="primary" icon={<PlusOutlined />} onClick={() => this.setState({ drawerVisible: true, editingProduct: null, fileList: [] })} /></Tooltip>
                            <Tooltip title="Danh mục"><Button className="mr-2" icon={<FolderOpenOutlined />} onClick={() => this.props.history.push('/product/cate')} /></Tooltip>
                            <Button
                                icon={<DatabaseOutlined />}
                                loading={this.state.knowledgePreparing}
                                onClick={this.prepareProductKnowledge}
                            >
                                Nạp kiến thức sản phẩm
                            </Button>
                        </div>
                        <ProductListFilters
                            onReady={(evt) => this.formFilter = evt}
                            categories={this.state.listCate}
                            onChange={() => {
                                this.page = 1;
                                this.getListData();
                            }}
                            onReset={() => {
                                if (this.formFilter) this.formFilter.resetFields();
                                this.page = 1;
                                this.getListData();
                            }}
                        />
                    </div>
                    <Spin spinning={this.state.loading}>
                        <ProductListTable
                            data={this.state.listData}
                            loading={this.state.loading}
                            page={this.page}
                            limit={this.limit}
                            total={this.state.total}
                            onPageChange={(page, size) => {
                                this.page = page;
                                this.limit = size || this.limit;
                                this.getListData();
                            }}
                            onEdit={(item) => this.setState({
                                drawerVisible: true,
                                editingProduct: item,
                                fileList: (item.attachments || []).map((attachment, index) => ({
                                    uid: `${index}`,
                                    status: 'done',
                                    url: attachment.url,
                                    type: attachment.type,
                                    name: attachment.name
                                }))
                            })}
                        />
                    </Spin>
                    <ProductDrawer
                        visible={this.state.drawerVisible}
                        onClose={() => this.setState({ drawerVisible: false, editingProduct: null, fileList: [] })}
                        product={this.state.editingProduct}
                        categories={this.state.listCate}
                        onSaveProduct={this.saveProduct}
                        fileList={this.state.fileList || []}
                        onFileChange={({ fileList }) => this.setState({ fileList })}
                        fetchData={fetchData}
                        notification={this.props.notification}
                        onRefresh={this.getListData}
                    />
                    <ProductKnowledgeReviewModal
                        visible={this.state.knowledgeReviewVisible}
                        loading={this.state.knowledgePreparing}
                        committing={this.state.knowledgeCommitting}
                        downloading={this.state.knowledgeDownloading}
                        draft={this.state.knowledgeDraft}
                        progress={this.state.knowledgeProgress}
                        document={this.state.knowledgeDocument}
                        documentLoading={this.state.knowledgeDocumentLoading}
                        documentError={this.state.knowledgeDocumentError}
                        onCancel={() => {
                            if (!this.state.knowledgePreparing && !this.state.knowledgeCommitting) {
                                this.setState({ knowledgeReviewVisible: false });
                            }
                        }}
                        onOpenDocument={this.downloadProductKnowledgeDocx}
                        onCommit={this.commitProductKnowledge}
                    />
                </div>
            </div>
        );
    }
}
